import { addSheetBackLink, PARENT_SHEETS } from "../sheetNavigation";

const HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FF33CCFF" },
};

const VALUE_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFFFFFF" },
};

const NA_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFD9D9D9" },
};

const THIN = { style: "thin", color: { argb: "FF000000" } };
const THIN_BLACK = { top: THIN, left: THIN, bottom: THIN, right: THIN };

const BODY_FONT = { name: "Calibri", size: 11 };
const TITLE_FONT = { name: "Calibri", size: 14, bold: true };
const HEADER_FONT = { ...BODY_FONT, bold: true };

const COL_START = 4; // D
const COL_NAME = 5; // E
const COL_DESC = 6; // F
const COL_COUNTRY = 7; // G
const COL_END = COL_COUNTRY;

const BACK_ROW = 7;
const TITLE_ROW = 8;
const HEADER_ROW = 9;
const TEMPLATE_ROW = 10;
const SPACER_ROW = 11;
const DATA_START_ROW = 12;

function setCell(ws, row, col, value, options = {}) {
  const cell = ws.getCell(row, col);
  cell.value = value;
  cell.font = options.bold ? HEADER_FONT : BODY_FONT;
  if (options.border) cell.border = options.border;
  else if (options.bordered !== false) cell.border = THIN_BLACK;
  cell.alignment = {
    horizontal: options.align || "center",
    vertical: "middle",
    wrapText: true,
  };
  if (options.fill) cell.fill = options.fill;
}

function borderHeaderRow(ws, row) {
  ws.getCell(row, COL_START).border = THIN_BLACK;
  ws.getCell(row, COL_NAME).border = { top: THIN, bottom: THIN, right: THIN };
  ws.getCell(row, COL_DESC).border = THIN_BLACK;
  ws.getCell(row, COL_COUNTRY).border = THIN_BLACK;
}

function borderDataRow(ws, row) {
  ws.getCell(row, COL_START).border = THIN_BLACK;
  ws.getCell(row, COL_NAME).border = THIN_BLACK;
  ws.getCell(row, COL_DESC).border = THIN_BLACK;
  ws.getCell(row, COL_COUNTRY).border = THIN_BLACK;
}

function borderSpacerRow(ws, row) {
  ws.mergeCells(row, COL_START, row, COL_END);
  ws.getCell(row, COL_START).border = { top: THIN, left: THIN, bottom: THIN, right: THIN };
}

function setDataRow(ws, row, index, entry) {
  borderDataRow(ws, row);

  setCell(ws, row, COL_START, String(index + 1), {
    align: "center",
    bordered: false,
  });

  setCell(ws, row, COL_NAME, entry.name, {
    align: "center",
    fill: VALUE_FILL,
    bordered: false,
  });

  setCell(ws, row, COL_DESC, entry.description, {
    align: "left",
    fill: NA_FILL,
    bordered: false,
  });

  setCell(ws, row, COL_COUNTRY, entry.country, {
    align: "left",
    fill: NA_FILL,
    bordered: false,
  });
}

function setTemplateRow(ws, row) {
  borderDataRow(ws, row);

  [COL_START, COL_NAME].forEach((col) => {
    setCell(ws, row, col, "", { align: "center", bordered: false });
  });

  [COL_DESC, COL_COUNTRY].forEach((col) => {
    setCell(ws, row, col, "", { align: "left", fill: NA_FILL, bordered: false });
  });
}

function normalizeExchanges(stockExchanges) {
  if (!stockExchanges) return [];
  const list = Array.isArray(stockExchanges) ? stockExchanges : [stockExchanges];

  return list
    .map((item) => {
      if (typeof item === "string") {
        return { name: item.trim(), description: "", country: "" };
      }
      if (typeof item === "object" && item !== null) {
        return {
          name: String(item.name || item.exchange || "").trim(),
          description: String(item.description || item.otherDescription || "").trim(),
          country: String(item.country || item.countryName || "").trim(),
        };
      }
      return { name: "", description: "", country: "" };
    })
    .filter((item) => item.name || item.description || item.country);
}

function setupStockExchangeSheet(ws) {
  const widths = {
    A: 9.1796875,
    B: 9.1796875,
    C: 9.1796875,
    D: 6.81640625,
    E: 35.54296875,
    F: 30.26953125,
    G: 27.81640625,
    H: 9.1796875,
    I: 9.1796875,
    J: 9.1796875,
  };

  Object.entries(widths).forEach(([col, width]) => {
    ws.getColumn(col).width = width;
  });
  ws.getColumn("A").hidden = true;
  ws.getColumn("B").hidden = true;
  // Visible through J; hide K → XFD.
  for (let col = 11; col <= 16384; col += 1) {
    const column = ws.getColumn(col);
    column.width = 9.1796875;
    column.hidden = true;
  }

  for (let r = 1; r <= 5; r += 1) {
    const row = ws.getRow(r);
    row.height = 15;
    row.hidden = true;
  }

  ws.views = [
    {
      showGridLines: false,
      state: "normal",
      zoomScale: 90,
    },
  ];
}

export function buildStockExchangeSheet(workbook, sectionA) {
  const ws = workbook.addWorksheet("Stock exchange");
  setupStockExchangeSheet(ws);

  addSheetBackLink(ws, PARENT_SHEETS.GENERAL_DISCLOSURES, {
    row: BACK_ROW,
    col: COL_END,
    align: "right",
  });

  const exchanges = normalizeExchanges(sectionA?.listedIdentity?.stockExchanges);

  ws.mergeCells(TITLE_ROW, COL_START, TITLE_ROW, COL_END);
  const tableTitle = ws.getCell(TITLE_ROW, COL_START);
  tableTitle.value = "Details of the Stock Exchanges";
  tableTitle.font = TITLE_FONT;
  tableTitle.fill = HEADER_FILL;
  tableTitle.border = THIN_BLACK;
  tableTitle.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  ws.getRow(TITLE_ROW).height = 30;

  const headers = [
    { col: COL_START, label: "Sr. No." },
    { col: COL_NAME, label: "Name of the Stock exchange" },
    { col: COL_DESC, label: "Description of other stock exchange" },
    { col: COL_COUNTRY, label: "Name of the Country" },
  ];

  borderHeaderRow(ws, HEADER_ROW);
  headers.forEach(({ col, label }) => {
    setCell(ws, HEADER_ROW, col, label, { bold: true, bordered: false });
  });

  setTemplateRow(ws, TEMPLATE_ROW);
  ws.getRow(TEMPLATE_ROW).height = 15;
  ws.getRow(TEMPLATE_ROW).hidden = true;

  borderSpacerRow(ws, SPACER_ROW);
  ws.getRow(SPACER_ROW).height = 30;

  const rows = exchanges.length ? exchanges : [];

  rows.forEach((entry, idx) => {
    setDataRow(ws, DATA_START_ROW + idx, idx, entry);
  });

  for (let r = 1; r <= 5; r += 1) {
    const row = ws.getRow(r);
    row.height = 15;
    row.hidden = true;
  }

  return ws;
}
